// Transparent mid-stream re-attach for the chat transport — the client half of
// the "Load failed" fix (see src/resumable-chat.mjs for the server half).
//
// WebKit surfaces a dead fetch stream as TypeError("Load failed") — weak
// cellular, network handoff, phone lock/backgrounding — and the stock ai-sdk
// transport turns that into a permanent visible error even though the server
// keeps producing the turn. This wrapper hides recoverable drops entirely:
// when the /api/chat response advertises x-resumable-stream-id and its body
// dies mid-read, we re-fetch the resume endpoint (which replays the turn's
// byte log from the start), skip the bytes already handed to the parser, and
// keep feeding the same ReadableStream. The UI never leaves the streaming
// state; only an unrecoverable drop (log expired, retry budget spent, user
// abort) surfaces the original error as before.
//
// Byte-offset skipping is sound because both the original response body and
// every replay are consumers of the same append-only server-side log — the
// byte sequences are identical by construction.

export const RESUMABLE_STREAM_ID_HEADER = 'x-resumable-stream-id';

// ~15s of backoff across 5 attempts; any delivered progress resets the
// budget, and a hidden→visible transition short-circuits the wait (iOS
// freezes timers while backgrounded, so the fast-path fires on foreground).
const MAX_RESUME_ATTEMPTS = 5;
const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 8_000;

export type ResilientFetchOptions = {
  /** Absolute or same-origin URL of the resume endpoint for a stream id. */
  resumeUrl: (streamId: string) => string;
  /** DELETE endpoint for deliberate stop/thread teardown; defaults to resumeUrl. */
  cancelUrl?: (streamId: string) => string;
  /** False for a follower of someone else's turn (collaboration v1): leaving
   * the stream never stops the turn, so no DELETE is sent. Default true. */
  cancelOnAbort?: boolean;
  /** Auth headers for the resume request (mirrors the transport's headers). */
  headers?: () => HeadersInit | Promise<HeadersInit>;
  /** Fires when the caller aborts or tears down the stream (user stop /
   * thread switch) — the moment a pending resume id stops being resurrectable. */
  onAborted?: (streamId: string, since: number) => void;
  /** Asked (after the abort's own event has settled) before the run is stopped on the server. Default: yes. */
  mayStopRun?: (since: number) => boolean;
  /** Stamps the start of each request/stream; `mayStopRun` is asked with that stamp. */
  begin?: () => number;
  baseFetch?: typeof fetch;
  /** An error that ends the action for good (e.g. the tab's session was replaced): never retried, and
   * no resume or cleanup request is sent after it. */
  terminalError?: (error: unknown) => boolean;
  /** Injectable backoff (tests). attempt is 1-based. */
  delayMs?: (attempt: number) => number;
};

type Resolved = Required<Pick<ResilientFetchOptions, 'resumeUrl' | 'baseFetch' | 'delayMs'>> &
  Pick<
    ResilientFetchOptions,
    | 'cancelUrl'
    | 'cancelOnAbort'
    | 'begin'
    | 'headers'
    | 'mayStopRun'
    | 'onAborted'
    | 'terminalError'
  >;

const defaultDelay = (attempt: number) =>
  Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS);

export function createResilientFetch(options: ResilientFetchOptions): typeof fetch {
  const resolved: Resolved = {
    baseFetch: options.baseFetch ?? globalThis.fetch.bind(globalThis),
    cancelUrl: options.cancelUrl,
    cancelOnAbort: options.cancelOnAbort,
    delayMs: options.delayMs ?? defaultDelay,
    resumeUrl: options.resumeUrl,
    headers: options.headers,
    onAborted: options.onAborted,
    mayStopRun: options.mayStopRun,
    begin: options.begin,
    terminalError: options.terminalError,
  };
  return async (input, init) => {
    const since = resolved.begin?.() ?? 0;
    const response = await resolved.baseFetch(input, init);
    const streamId = response.headers.get(RESUMABLE_STREAM_ID_HEADER);
    if (!response.ok || !response.body || !streamId) return response;
    const body = resumingStream(response.body, streamId, init?.signal ?? null, resolved, since);
    return new Response(body, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
  };
}

function resumingStream(
  body: ReadableStream<Uint8Array>,
  streamId: string,
  signal: AbortSignal | null,
  options: Resolved,
  since: number,
): ReadableStream<Uint8Array> {
  const state = {
    since,
    reader: body.getReader(),
    delivered: 0,
    attempts: 0,
    abortNotified: false,
  };

  const readOrReattach = async (): Promise<ReadableStreamReadResult<Uint8Array>> => {
    // Loop so a re-attached reader whose FIRST read also fails goes back
    // through the retry guard instead of escaping it. The attempt budget in
    // `state` is shared across iterations and only refills on delivered
    // progress, so this cannot spin.
    for (;;) {
      try {
        return await state.reader.read();
      } catch (err) {
        if (signal?.aborted) {
          notifyAborted(streamId, state, options);
          throw err;
        }
        // Terminal (e.g. a retired session): the original error surfaces; nothing is resumed or cancelled.
        if (options.terminalError?.(err)) {
          state.abortNotified = true;
          throw err;
        }
        const next = await reattach(streamId, state, signal, options);
        if (!next) throw err;
        state.reader = next.reader;
        if (next.remainder) return { done: false, value: next.remainder };
      }
    }
  };

  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      const { done, value } = await readOrReattach();
      if (done) {
        controller.close();
        return;
      }
      state.delivered += value.byteLength;
      state.attempts = 0;
      controller.enqueue(value);
    },
    cancel(reason) {
      notifyAborted(streamId, state, options);
      void state.reader.cancel(reason).catch(() => {});
    },
  });
}

type Reattached = { reader: ReadableStreamDefaultReader<Uint8Array>; remainder: Uint8Array | null };

/** Re-fetch the replay endpoint and fast-forward past the delivered bytes.
 * Null means give up and surface the original error: the log is gone (204),
 * the budget is spent, or the caller aborted. */
async function reattach(
  streamId: string,
  state: { delivered: number; attempts: number; abortNotified: boolean; since: number },
  signal: AbortSignal | null,
  options: Resolved,
): Promise<Reattached | null> {
  if (signal?.aborted) {
    notifyAborted(streamId, state, options);
    return null;
  }
  while (state.attempts < MAX_RESUME_ATTEMPTS) {
    state.attempts += 1;
    await interruptibleDelay(options.delayMs(state.attempts), signal);
    if (signal?.aborted) {
      notifyAborted(streamId, state, options);
      return null;
    }
    const attempt = await tryResumeOnce(streamId, state.delivered, signal, options);
    if (attempt.terminal) return null;
    if (signal?.aborted) {
      notifyAborted(streamId, state, options);
      return null;
    }
    if (attempt.gone) return null;
    if (attempt.result) return attempt.result;
  }
  return null;
}

function notifyAborted(
  streamId: string,
  state: { abortNotified: boolean; since: number },
  options: Resolved,
): void {
  if (state.abortNotified) return;
  state.abortNotified = true;
  options.onAborted?.(streamId, state.since);
  if (options.cancelOnAbort !== false) void cancelResume(streamId, options, state.since);
}

async function cancelResume(streamId: string, options: Resolved, since: number): Promise<void> {
  // The Stop click and the abort it causes run in the same event; let both settle before asking.
  if (options.mayStopRun) {
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (!options.mayStopRun(since)) return;
  }
  try {
    await options.baseFetch((options.cancelUrl ?? options.resumeUrl)(streamId), {
      method: 'DELETE',
      headers: (await options.headers?.()) ?? {},
    });
  } catch {
    // Best-effort cleanup: a failed cancel must not mask the user's local stop.
  }
}

async function tryResumeOnce(
  streamId: string,
  delivered: number,
  signal: AbortSignal | null,
  options: Resolved,
): Promise<{ result?: Reattached; gone?: boolean; terminal?: boolean }> {
  try {
    const res = await options.baseFetch(options.resumeUrl(streamId), {
      method: 'GET',
      headers: (await options.headers?.()) ?? {},
      ...(signal ? { signal } : {}),
    });
    if (res.status === 204) return { gone: true };
    if (!res.ok || !res.body) return {};
    const reader = res.body.getReader();
    const skipped = await skipDelivered(reader, delivered);
    if (!skipped) return {};
    return { result: { reader, remainder: skipped.remainder } };
  } catch (err) {
    if (options.terminalError?.(err)) return { terminal: true };
    return signal?.aborted ? { gone: true } : {};
  }
}

/** Consume `count` bytes of the replay; return the tail of a straddling chunk.
 * Null when the replay ends short of `count` — an inconsistent log, retry. */
async function skipDelivered(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  count: number,
): Promise<{ remainder: Uint8Array | null } | null> {
  let seen = 0;
  while (seen < count) {
    const { done, value } = await reader.read();
    if (done) return null;
    seen += value.byteLength;
    if (seen > count) return { remainder: value.subarray(value.byteLength - (seen - count)) };
  }
  return { remainder: null };
}

/** Backoff sleep that ends early on abort or on the page becoming visible
 * again (retry immediately when iOS un-freezes us on foreground). */
function interruptibleDelay(ms: number, signal: AbortSignal | null): Promise<void> {
  return new Promise((resolve) => {
    const doc = typeof document === 'undefined' ? null : document;
    const finish = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', finish);
      doc?.removeEventListener('visibilitychange', onVisible);
      resolve();
    };
    const onVisible = () => {
      if (doc?.visibilityState === 'visible') finish();
    };
    const timer = setTimeout(finish, ms);
    signal?.addEventListener('abort', finish, { once: true });
    doc?.addEventListener('visibilitychange', onVisible);
  });
}
